import { useState, useEffect, useCallback } from 'react'
import { listTasks, CURRENT_DATASET_VERSION } from '../engine/TaskBuilder'
import { runAgent, getApiKey, type AgentMode, type AgentRunConfig } from '../engine/AgentBridge'
import { calculateMultiDimScore, type MultiDimScore } from '../engine/BenchmarkEngine'
import { calculateLLMJudgeScore, getUseLLMJudge, type JudgeScore } from '../engine/LLMJudge'
import type { Task, Trace } from '../engine/types'
import { useLang } from '../i18n/LangContext'
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, Legend, ResponsiveContainer } from 'recharts'
import {
  Play, Settings, Plus, Trash2, CheckCircle, Loader, Download, BarChart3, Table, Target,
  Save, Upload, TrendingUp, Sigma, Share2, Check, Scale,
} from 'lucide-react'

// ---- Statistics helpers ----
function calcStdDev(values: number[]): number {
  if (values.length < 2) return 0
  const mean = values.reduce((s, v) => s + v, 0) / values.length
  const variance = values.reduce((s, v) => s + (v - mean) ** 2, 0) / (values.length - 1)
  return Math.sqrt(variance)
}

function calcConfidenceInterval(values: number[]): { lower: number; upper: number } | null {
  if (values.length < 2) return null
  const mean = values.reduce((s, v) => s + v, 0) / values.length
  const std = calcStdDev(values)
  const se = std / Math.sqrt(values.length)
  const margin = 1.96 * se
  return { lower: mean - margin, upper: mean + margin }
}

function calcTTest(values1: number[], values2: number[]): { t: number; significant: boolean } | null {
  if (values1.length < 2 || values2.length < 2) return null
  const mean1 = values1.reduce((s, v) => s + v, 0) / values1.length
  const mean2 = values2.reduce((s, v) => s + v, 0) / values2.length
  const std1 = calcStdDev(values1)
  const std2 = calcStdDev(values2)
  const n1 = values1.length
  const n2 = values2.length
  const se = Math.sqrt(std1 ** 2 / n1 + std2 ** 2 / n2)
  if (se === 0) return null
  const t = (mean1 - mean2) / se
  // df approximation (Welch's)
  const df = Math.min(n1 + n2 - 2, 30)
  // Critical t-value for p<0.05, two-tailed, df>=10 is ~2.0
  const criticalT = df >= 30 ? 1.96 : df >= 10 ? 2.04 : 2.23
  return { t: Math.abs(t), significant: Math.abs(t) > criticalT }
}

// ---- Config YAML generation ----
function generateConfigYAML(selectedTaskIds: string[], agents: AgentRow[], maxSteps: number): string {
  const lines: string[] = []
  lines.push('# DecisionCraft Evaluation Configuration')
  lines.push(`generated_at: ${new Date().toISOString()}`)
  lines.push(`dataset_version: ${CURRENT_DATASET_VERSION}`)
  lines.push('')
  lines.push('tasks:')
  for (const id of selectedTaskIds) {
    lines.push(`  - ${id}`)
  }
  lines.push('')
  lines.push('agents:')
  for (const a of agents) {
    lines.push(`  - name: "${a.name}"`)
    lines.push(`    enabled: ${a.enabled}`)
    lines.push(`    mode: ${a.mode}`)
    lines.push(`    model: ${a.model}`)
    if (a.webhook_url) lines.push(`    webhook_url: "${a.webhook_url}"`)
  }
  lines.push('')
  lines.push(`max_steps: ${maxSteps}`)
  lines.push('temperature: 0')
  return lines.join('\n')
}

function parseConfigYAML(yaml: string): { taskIds: string[]; agents: Partial<AgentRow>[]; maxSteps: number } | null {
  try {
    const lines = yaml.split('\n')
    const taskIds: string[] = []
    const agents: Partial<AgentRow>[] = []
    let maxSteps = 25
    let currentAgent: Partial<AgentRow> | null = null
    let inTasks = false
    let inAgents = false

    for (const line of lines) {
      const trimmed = line.trim()
      if (trimmed.startsWith('#') || !trimmed) continue

      if (trimmed === 'tasks:') { inTasks = true; inAgents = false; continue }
      if (trimmed === 'agents:') { inTasks = false; inAgents = true; continue }
      if (trimmed.startsWith('max_steps:')) {
        maxSteps = parseInt(trimmed.split(':')[1].trim()) || 25
        continue
      }

      if (inTasks && trimmed.startsWith('- ')) {
        taskIds.push(trimmed.slice(2).trim())
      }
      if (inAgents) {
        if (trimmed.startsWith('- name:')) {
          if (currentAgent) agents.push(currentAgent)
          currentAgent = { name: trimmed.split('"')[1] || `Agent ${agents.length}`, mode: 'simulation', model: '', enabled: true }
        } else if (currentAgent) {
          if (trimmed.startsWith('mode:')) currentAgent.mode = trimmed.split(':')[1].trim() as AgentMode
          else if (trimmed.startsWith('model:')) currentAgent.model = trimmed.split(':')[1].trim()
          else if (trimmed.startsWith('enabled:')) currentAgent.enabled = trimmed.split(':')[1].trim() === 'true'
          else if (trimmed.startsWith('webhook_url:')) currentAgent.webhook_url = trimmed.split('"')[1] || ''
        }
      }
    }
    if (currentAgent) agents.push(currentAgent)
    return { taskIds, agents, maxSteps }
  } catch {
    return null
  }
}

// ---- Types ----
interface AgentRow {
  id: string
  enabled: boolean
  name: string
  mode: AgentMode
  model: string
  webhook_url?: string
}

interface ResultCell {
  trace: Trace | null
  score: MultiDimScore | null
  error: string | null
  judge?: JudgeScore | null
}

interface CurrentRun {
  taskIndex: number
  agentIndex: number
  totalTasks: number
  totalAgents: number
  taskId: string
  taskName: string
  agentName: string
  done: number
  total: number
}

// ---- Constants ----
let _rowId = 0
const uid = () => `row-${Date.now()}-${_rowId++}`

const MODEL_OPTIONS: Record<AgentMode, string[]> = {
  simulation: ['gpt-4o-sim', 'claude-3.5-sim', 'deepseek-v3-sim'],
  openai: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo'],
  anthropic: ['claude-3-5-sonnet-20241022', 'claude-3-5-haiku-20241022'],
  webhook: [],
}

const DEFAULT_MODEL: Record<AgentMode, string> = {
  simulation: 'gpt-4o-sim',
  openai: 'gpt-4o',
  anthropic: 'claude-3-5-sonnet-20241022',
  webhook: '',
}

const AGENT_COLORS = ['#6c8cff', '#4ade80', '#fbbf24', '#f87171']

const MAX_AGENTS = 4

const DIMENSIONS = [
  { key: 'accuracy', zh: '准确性', en: 'Accuracy', max: 40 },
  { key: 'process', zh: '过程', en: 'Process', max: 30 },
  { key: 'efficiency', zh: '效率', en: 'Efficiency', max: 20 },
  { key: 'constraint', zh: '约束', en: 'Constraint', max: 10 },
] as const

// ---- Helpers ----
function scoreColor(total: number): string {
  if (total >= 80) return 'var(--color-success)'
  if (total >= 60) return 'var(--color-warning)'
  return 'var(--color-danger)'
}

function makeDefaultAgents(): AgentRow[] {
  return [
    { id: uid(), enabled: true, name: 'Agent A', mode: 'simulation', model: 'gpt-4o-sim' },
    { id: uid(), enabled: true, name: 'Agent B', mode: 'simulation', model: 'claude-3.5-sim' },
  ]
}

// ---- Component ----
export default function BatchEvaluationPage() {
  const { lang } = useLang()
  const tr = (zh: string, en: string) => (lang === 'zh' ? zh : en)

  const [tasks, setTasks] = useState<Task[]>([])
  const [tasksLoading, setTasksLoading] = useState(true)
  const [tasksError, setTasksError] = useState<string | null>(null)
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([])
  const [agents, setAgents] = useState<AgentRow[]>(makeDefaultAgents)
  const [maxSteps, setMaxSteps] = useState(25)

  const [results, setResults] = useState<Record<string, ResultCell[]>>({})
  const [running, setRunning] = useState(false)
  const [currentRun, setCurrentRun] = useState<CurrentRun | null>(null)
  const [completed, setCompleted] = useState(false)
  const [configError, setConfigError] = useState<string | null>(null)
  const [selectedCell, setSelectedCell] = useState<{ taskId: string; agentIndex: number } | null>(null)

  // ---- Load tasks ----
  const fetchTasks = useCallback(async () => {
    setTasksLoading(true)
    setTasksError(null)
    try {
      const data = await listTasks()
      setTasks(data)
    } catch (err) {
      setTasksError(err instanceof Error ? err.message : 'Failed to load tasks')
    } finally {
      setTasksLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTasks()
  }, [fetchTasks])

  // ---- Task selection helpers ----
  const toggleTask = (id: string) => {
    setSelectedTaskIds(prev =>
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id],
    )
  }
  const selectAllTasks = () => setSelectedTaskIds(tasks.map(t => t.id))
  const clearTasks = () => setSelectedTaskIds([])

  // ---- Agent helpers ----
  const addAgent = () => {
    if (agents.length >= MAX_AGENTS) return
    const idx = agents.length
    setAgents(prev => [
      ...prev,
      {
        id: uid(),
        enabled: true,
        name: `Agent ${String.fromCharCode(65 + idx)}`,
        mode: 'simulation',
        model: 'gpt-4o-sim',
      },
    ])
  }

  const removeAgent = (id: string) => {
    if (agents.length <= 1) return
    setAgents(prev => prev.filter(a => a.id !== id))
  }

  const updateAgent = (id: string, patch: Partial<AgentRow>) => {
    setAgents(prev => prev.map(a => (a.id === id ? { ...a, ...patch } : a)))
  }

  const changeAgentMode = (id: string, mode: AgentMode) => {
    setAgents(prev =>
      prev.map(a =>
        a.id === id
          ? { ...a, mode, model: DEFAULT_MODEL[mode], webhook_url: mode === 'webhook' ? a.webhook_url ?? '' : a.webhook_url }
          : a,
      ),
    )
  }

  // ---- Derived data ----
  const enabledAgents = agents.filter(a => a.enabled)
  const selectedTasks = tasks.filter(t => selectedTaskIds.includes(t.id))

  // Per-agent averages
  const averages = enabledAgents.map((_agent, ai) => {
    const cells = selectedTasks
      .map(t => results[t.id]?.[ai])
      .filter((c): c is ResultCell => !!c && !!c.score)
    if (cells.length === 0) return null
    const avgTotal = cells.reduce((s, c) => s + (c.score!.total), 0) / cells.length
    const breakdown = {
      accuracy: cells.reduce((s, c) => s + c.score!.breakdown.accuracy, 0) / cells.length,
      process: cells.reduce((s, c) => s + c.score!.breakdown.process, 0) / cells.length,
      efficiency: cells.reduce((s, c) => s + c.score!.breakdown.efficiency, 0) / cells.length,
      constraint: cells.reduce((s, c) => s + c.score!.breakdown.constraint, 0) / cells.length,
    }
    return { avgTotal, breakdown }
  })

  // Radar chart data (average breakdown % per dimension)
  const radarData = DIMENSIONS.map(dim => {
    const row: Record<string, number | string> = { dimension: tr(dim.zh, dim.en) }
    enabledAgents.forEach((agent, ai) => {
      row[agent.name] = averages[ai] ? Math.round(averages[ai]!.breakdown[dim.key]) : 0
    })
    return row
  })

  // ---- Run a single task/agent pair via callbacks-based runAgent ----
  const runOne = (
    task: Task,
    config: AgentRunConfig,
  ): Promise<{ trace: Trace | null; error: string | null }> =>
    new Promise(resolve => {
      let settled = false
      const done = (res: { trace: Trace | null; error: string | null }) => {
        if (!settled) {
          settled = true
          resolve(res)
        }
      }
      runAgent(task, config, {
        onStep: () => {},
        onComplete: trace => done({ trace, error: null }),
        onError: error => done({ trace: null, error }),
      }).catch(err => {
        done({ trace: null, error: err instanceof Error ? err.message : 'Unknown error' })
      })
    })

  // ---- Batch run ----
  const runBatch = async () => {
    if (selectedTasks.length === 0) {
      setConfigError(tr('请至少选择一个任务', 'Please select at least one task'))
      return
    }
    if (enabledAgents.length === 0) {
      setConfigError(tr('请至少启用一个Agent', 'Please enable at least one agent'))
      return
    }

    setConfigError(null)
    setRunning(true)
    setCompleted(false)
    setResults({})
    setCurrentRun(null)

    const init: Record<string, ResultCell[]> = {}
    for (const t of selectedTasks) {
      init[t.id] = enabledAgents.map(() => ({ trace: null, score: null, error: null }))
    }
    setResults(init)

    const totalTasks = selectedTasks.length
    const totalAgents = enabledAgents.length
    const total = totalTasks * totalAgents
    let doneCount = 0

    for (let ti = 0; ti < selectedTasks.length; ti++) {
      const task = selectedTasks[ti]
      for (let ai = 0; ai < enabledAgents.length; ai++) {
        const row = enabledAgents[ai]
        setCurrentRun({
          taskIndex: ti,
          agentIndex: ai,
          totalTasks,
          totalAgents,
          taskId: task.id,
          taskName: task.title,
          agentName: row.name,
          done: doneCount,
          total,
        })

        const config: AgentRunConfig = {
          mode: row.mode,
          name: row.name,
          model: row.model || undefined,
          max_steps: maxSteps,
          ...(row.mode === 'openai' ? { api_key: getApiKey('openai') } : {}),
          ...(row.mode === 'anthropic' ? { api_key: getApiKey('anthropic') } : {}),
          ...(row.mode === 'webhook' ? { webhook_url: row.webhook_url } : {}),
        }

        const { trace, error } = await runOne(task, config)
        const score = trace ? calculateMultiDimScore(trace, task) : null

        // Optional LLM-as-a-Judge scoring
        let judge: JudgeScore | null = null
        if (trace && getUseLLMJudge()) {
          judge = await calculateLLMJudgeScore(trace, task)
        }

        setResults(prev => {
          const next = { ...prev }
          const arr = [...(next[task.id] ?? [])]
          arr[ai] = { trace, score, error, judge }
          next[task.id] = arr
          return next
        })

        doneCount++
      }
    }

    setCurrentRun(null)
    setRunning(false)
    setCompleted(true)
  }

  // ---- CSV export ----
  const exportCSV = () => {
    const esc = (s: string) => (/["',\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s)
    const header = [tr('任务', 'Task'), ...enabledAgents.map(a => a.name)].join(',')
    const rows = selectedTasks.map(t => {
      const cells = enabledAgents.map((_a, ai) => {
        const c = results[t.id]?.[ai]
        if (c?.score) return String(c.score.total)
        if (c?.error) return 'ERROR'
        return '-'
      })
      return [esc(t.title), ...cells].join(',')
    })
    const avgCells = enabledAgents.map((_a, ai) => {
      const cells = selectedTasks
        .map(t => results[t.id]?.[ai])
        .filter((c): c is ResultCell => !!c && !!c.score)
      if (cells.length === 0) return '-'
      const avg = cells.reduce((s, c) => s + c.score!.total, 0) / cells.length
      return avg.toFixed(1)
    })
    const avgRow = [tr('平均', 'Average'), ...avgCells].join(',')
    const csv = [header, ...rows, avgRow].join('\n')

    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `batch-evaluation-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // ---- Share results via URL hash (base64-encoded JSON) ----
  const [shareCopied, setShareCopied] = useState(false)
  const shareResults = () => {
    interface SharedAgent {
      name: string
      mode: string
      model?: string
      avg_total: number
      breakdown: { accuracy: number; process: number; efficiency: number; constraint: number }
      scores: number[]
      stats?: { mean: number; std: number; ci_lower?: number; ci_upper?: number }
      judge_avg?: number | null
    }
    interface SharedResult {
      generated_at: string
      dataset_version: string
      tasks: Array<{ id: string; title: string }>
      agents: SharedAgent[]
      max_steps: number
      ttests?: Array<{ a: string; b: string; t: number; significant: boolean }>
    }

    const agents: SharedAgent[] = enabledAgents.map((a, ai) => {
      const cells = selectedTasks
        .map(t => results[t.id]?.[ai])
        .filter((c): c is ResultCell => !!c && !!c.score)
      const scores = cells.map(c => c.score!.total)
      const avgTotal = scores.length > 0 ? scores.reduce((s, v) => s + v, 0) / scores.length : 0
      const breakdown = {
        accuracy: cells.length > 0 ? cells.reduce((s, c) => s + c.score!.breakdown.accuracy, 0) / cells.length : 0,
        process: cells.length > 0 ? cells.reduce((s, c) => s + c.score!.breakdown.process, 0) / cells.length : 0,
        efficiency: cells.length > 0 ? cells.reduce((s, c) => s + c.score!.breakdown.efficiency, 0) / cells.length : 0,
        constraint: cells.length > 0 ? cells.reduce((s, c) => s + c.score!.breakdown.constraint, 0) / cells.length : 0,
      }
      const std = calcStdDev(scores)
      const ci = calcConfidenceInterval(scores)
      // Judge scores
      const judgeCells = cells.filter(c => c.judge && !c.judge.error)
      const judgeAvg = judgeCells.length > 0
        ? Math.round(judgeCells.reduce((s, c) => s + (c.judge!.overall), 0) / judgeCells.length)
        : null
      return {
        name: a.name,
        mode: a.mode,
        model: a.model || undefined,
        avg_total: avgTotal,
        breakdown,
        scores,
        stats: scores.length > 1 ? { mean: avgTotal, std, ci_lower: ci?.lower, ci_upper: ci?.upper } : undefined,
        judge_avg: judgeAvg,
      }
    })

    // Pairwise t-tests
    const ttests: Array<{ a: string; b: string; t: number; significant: boolean }> = []
    for (let i = 0; i < agents.length; i++) {
      for (let j = i + 1; j < agents.length; j++) {
        const tt = calcTTest(agents[i].scores, agents[j].scores)
        if (tt) {
          ttests.push({ a: agents[i].name, b: agents[j].name, t: tt.t, significant: tt.significant })
        }
      }
    }

    const payload: SharedResult = {
      generated_at: new Date().toISOString(),
      dataset_version: CURRENT_DATASET_VERSION,
      tasks: selectedTasks.map(t => ({ id: t.id, title: t.title })),
      agents,
      max_steps: maxSteps,
      ttests: ttests.length > 0 ? ttests : undefined,
    }

    // Encode base64 (UTF-8 safe)
    const json = JSON.stringify(payload)
    const b64 = btoa(unescape(encodeURIComponent(json)))
    const shareUrl = `${window.location.origin}/share#${b64}`

    // Copy to clipboard
    navigator.clipboard.writeText(shareUrl).then(() => {
      setShareCopied(true)
      setTimeout(() => setShareCopied(false), 2500)
    }).catch(() => {
      // Fallback: open in new tab
      window.open(shareUrl, '_blank')
    })
  }

  const progress = currentRun
    ? Math.round(((currentRun.done + 1) / currentRun.total) * 100)
    : completed
      ? 100
      : 0

  // ---- Loading state ----
  if (tasksLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader size={32} className="animate-spin" style={{ color: 'var(--color-accent)' }} />
          <p style={{ color: 'var(--color-text-secondary)' }}>{tr('加载中...', 'Loading...')}</p>
        </div>
      </div>
    )
  }

  if (tasksError && tasks.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4 max-w-md text-center px-4">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'rgba(248,113,113,0.12)' }}
          >
            <span style={{ color: 'var(--color-danger)' }}>!</span>
          </div>
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            {tr('加载失败', 'Failed to Load')}
          </h3>
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{tasksError}</p>
          <button
            onClick={fetchTasks}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
            style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
          >
            {tr('重试', 'Retry')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="container-page px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
            {tr('批量评测', 'Batch Evaluation')}
          </h1>
          <span
            className="px-2 py-0.5 rounded text-xs font-semibold"
            style={{
              backgroundColor: 'rgba(108,140,255,0.12)',
              color: 'var(--color-accent)',
              border: '1px solid rgba(108,140,255,0.25)',
            }}
            title={tr('数据集版本，用于论文引用和可复现性', 'Dataset version for paper citation and reproducibility')}
          >
            {tr('数据集', 'Dataset')} {CURRENT_DATASET_VERSION}
          </span>
          <span
            className="px-2 py-0.5 rounded text-xs font-semibold"
            style={{
              backgroundColor: 'rgba(251,191,36,0.12)',
              color: 'var(--color-warning)',
              border: '1px solid rgba(251,191,36,0.25)',
            }}
          >
            Beta
          </span>
        </div>
        <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          {tr('多任务×多模型对比评测', 'Multi-task × Multi-model comparative evaluation')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* ============ Left: Configuration Panel (~40%) ============ */}
        <div
          className="rounded-xl p-6 lg:col-span-2"
          style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}
        >
          <h2
            className="text-lg font-semibold mb-6 flex items-center gap-2"
            style={{ color: 'var(--color-text-primary)' }}
          >
            <Settings size={18} style={{ color: 'var(--color-accent)' }} />
            {tr('配置', 'Configuration')}
          </h2>

          {configError && (
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-lg mb-4 text-sm"
              style={{
                backgroundColor: 'rgba(248,113,113,0.1)',
                color: 'var(--color-danger)',
                border: '1px solid rgba(248,113,113,0.25)',
              }}
            >
              {configError}
            </div>
          )}

          <div className="space-y-6">
            {/* Task Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                  {tr('选择任务', 'Select Tasks')}
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={selectAllTasks}
                    disabled={running}
                    className="text-xs px-2 py-1 rounded"
                    style={{
                      backgroundColor: 'var(--color-bg-tertiary)',
                      color: 'var(--color-text-secondary)',
                      opacity: running ? 0.5 : 1,
                    }}
                  >
                    {tr('全选', 'Select All')}
                  </button>
                  <button
                    onClick={clearTasks}
                    disabled={running}
                    className="text-xs px-2 py-1 rounded"
                    style={{
                      backgroundColor: 'var(--color-bg-tertiary)',
                      color: 'var(--color-text-secondary)',
                      opacity: running ? 0.5 : 1,
                    }}
                  >
                    {tr('清空', 'Clear')}
                  </button>
                </div>
              </div>
              <div
                className="rounded-lg p-2 max-h-56 overflow-y-auto space-y-1"
                style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)' }}
              >
                {tasks.map(task => {
                  const checked = selectedTaskIds.includes(task.id)
                  return (
                    <label
                      key={task.id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-black/20"
                      style={{ opacity: running ? 0.6 : 1 }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleTask(task.id)}
                        disabled={running}
                        className="accent-[var(--color-accent)]"
                      />
                      <span className="text-sm flex-1 truncate" style={{ color: 'var(--color-text-primary)' }}>
                        {task.title}
                      </span>
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded shrink-0"
                        style={{ backgroundColor: 'var(--color-accent-glow)', color: 'var(--color-accent)' }}
                      >
                        {task.difficulty}
                      </span>
                    </label>
                  )
                })}
              </div>
              <p className="mt-1.5 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                {tr(`已选 ${selectedTaskIds.length}/${tasks.length}`, `Selected ${selectedTaskIds.length}/${tasks.length}`)}
              </p>
            </div>

            {/* Agent Configuration */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                {tr('选择Agent', 'Select Agents')}
              </label>
              <div className="space-y-2">
                {agents.map((agent, ai) => (
                  <div
                    key={agent.id}
                    className="rounded-lg p-3 space-y-2"
                    style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)' }}
                  >
                    {/* Row 1: enable + name + delete */}
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={agent.enabled}
                        onChange={e => updateAgent(agent.id, { enabled: e.target.checked })}
                        disabled={running}
                        className="accent-[var(--color-accent)] shrink-0"
                      />
                      <span
                        className="w-3 h-3 rounded-full shrink-0"
                        style={{ backgroundColor: AGENT_COLORS[ai % AGENT_COLORS.length] }}
                      />
                      <input
                        type="text"
                        value={agent.name}
                        onChange={e => updateAgent(agent.id, { name: e.target.value })}
                        disabled={running}
                        placeholder={tr('Agent名称', 'Agent name')}
                        className="flex-1 px-2 py-1.5 rounded text-sm outline-none min-w-0"
                        style={{
                          backgroundColor: 'var(--color-bg-primary)',
                          color: 'var(--color-text-primary)',
                          border: '1px solid var(--color-border)',
                        }}
                      />
                      {agents.length > 1 && (
                        <button
                          onClick={() => removeAgent(agent.id)}
                          disabled={running}
                          className="p-1.5 rounded shrink-0"
                          style={{ color: 'var(--color-danger)', opacity: running ? 0.5 : 1 }}
                          title={tr('删除', 'Delete')}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                    {/* Row 2: mode + model/webhook */}
                    <div className="flex items-center gap-2">
                      <select
                        value={agent.mode}
                        onChange={e => changeAgentMode(agent.id, e.target.value as AgentMode)}
                        disabled={running}
                        className="px-2 py-1.5 rounded text-sm outline-none appearance-none"
                        style={{
                          backgroundColor: 'var(--color-bg-primary)',
                          color: 'var(--color-text-primary)',
                          border: '1px solid var(--color-border)',
                        }}
                      >
                        <option value="simulation">Simulation</option>
                        <option value="openai">OpenAI</option>
                        <option value="anthropic">Anthropic</option>
                        <option value="webhook">Webhook</option>
                      </select>
                      {agent.mode === 'webhook' ? (
                        <input
                          type="text"
                          value={agent.webhook_url ?? ''}
                          onChange={e => updateAgent(agent.id, { webhook_url: e.target.value })}
                          disabled={running}
                          placeholder="https://..."
                          className="flex-1 px-2 py-1.5 rounded text-sm outline-none min-w-0"
                          style={{
                            backgroundColor: 'var(--color-bg-primary)',
                            color: 'var(--color-text-primary)',
                            border: '1px solid var(--color-border)',
                          }}
                        />
                      ) : (
                        <select
                          value={agent.model}
                          onChange={e => updateAgent(agent.id, { model: e.target.value })}
                          disabled={running}
                          className="flex-1 px-2 py-1.5 rounded text-sm outline-none appearance-none min-w-0"
                          style={{
                            backgroundColor: 'var(--color-bg-primary)',
                            color: 'var(--color-text-primary)',
                            border: '1px solid var(--color-border)',
                          }}
                        >
                          {MODEL_OPTIONS[agent.mode].map(m => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {agents.length < MAX_AGENTS && (
                <button
                  onClick={addAgent}
                  disabled={running}
                  className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium"
                  style={{
                    backgroundColor: 'var(--color-accent-glow)',
                    color: 'var(--color-accent)',
                    border: '1px solid var(--color-accent)',
                    opacity: running ? 0.5 : 1,
                  }}
                >
                  <Plus size={14} />
                  {tr('添加Agent', 'Add Agent')}
                </button>
              )}
            </div>

            {/* Run Settings */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                {tr('每任务最大步骤', 'Max steps per task')}
              </label>
              <input
                type="number"
                min={1}
                max={100}
                value={maxSteps}
                onChange={e => setMaxSteps(Math.max(1, Number(e.target.value) || 1))}
                disabled={running}
                className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                style={{
                  backgroundColor: 'var(--color-bg-tertiary)',
                  color: 'var(--color-text-primary)',
                  border: '1px solid var(--color-border)',
                  opacity: running ? 0.5 : 1,
                }}
              />
            </div>

            <button
              onClick={runBatch}
              disabled={running || selectedTasks.length === 0 || enabledAgents.length === 0}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-colors"
              style={{
                backgroundColor: running ? 'var(--color-border)' : 'var(--color-accent)',
                color: '#fff',
                opacity: running || selectedTasks.length === 0 || enabledAgents.length === 0 ? 0.5 : 1,
                cursor: running ? 'not-allowed' : 'pointer',
              }}
            >
              <Play size={16} />
              {tr('开始批量评测', 'Start Batch Evaluation')}
            </button>

            {/* Config Save/Load */}
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => {
                  const yaml = generateConfigYAML(selectedTaskIds, agents, maxSteps)
                  const blob = new Blob([yaml], { type: 'text/yaml;charset=utf-8;' })
                  const url = URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = `evaluation-config-${new Date().toISOString().slice(0, 10)}.yaml`
                  document.body.appendChild(a)
                  a.click()
                  document.body.removeChild(a)
                  URL.revokeObjectURL(url)
                }}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium"
                style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
              >
                <Save size={14} />
                {tr('保存配置', 'Save Config')}
              </button>
              <label
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium cursor-pointer"
                style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
              >
                <Upload size={14} />
                {tr('加载配置', 'Load Config')}
                <input
                  type="file"
                  accept=".yaml,.yml,.txt"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (!file) return
                    const reader = new FileReader()
                    reader.onload = (ev) => {
                      const yaml = String(ev.target?.result || '')
                      const parsed = parseConfigYAML(yaml)
                      if (parsed) {
                        setSelectedTaskIds(parsed.taskIds.filter(id => tasks.some(t => t.id === id)))
                        if (parsed.agents.length > 0) {
                          setAgents(parsed.agents.map((a, i) => ({
                            id: `row-${Date.now()}-${i}`,
                            enabled: a.enabled ?? true,
                            name: a.name || `Agent ${i}`,
                            mode: a.mode || 'simulation',
                            model: a.model || DEFAULT_MODEL[a.mode || 'simulation'],
                            webhook_url: a.webhook_url,
                          })))
                        }
                        setMaxSteps(parsed.maxSteps)
                      }
                    }
                    reader.readAsText(file)
                  }}
                />
              </label>
            </div>
          </div>
        </div>

        {/* ============ Right: Results Panel (~60%) ============ */}
        <div
          className="rounded-xl p-6 lg:col-span-3 flex flex-col"
          style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2
              className="text-lg font-semibold flex items-center gap-2"
              style={{ color: 'var(--color-text-primary)' }}
            >
              <BarChart3 size={18} style={{ color: 'var(--color-accent)' }} />
              {tr('评测结果', 'Results')}
            </h2>
            {completed && enabledAgents.length > 0 && selectedTasks.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={shareResults}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                  style={{
                    backgroundColor: shareCopied ? 'rgba(74,222,128,0.15)' : 'var(--color-accent)',
                    color: shareCopied ? 'var(--color-success)' : '#fff',
                    border: '1px solid transparent',
                  }}
                  title={tr('生成可分享链接', 'Generate shareable link')}
                >
                  {shareCopied ? <Check size={13} /> : <Share2 size={13} />}
                  {shareCopied ? (tr('链接已复制', 'Link Copied!')) : (tr('分享结果', 'Share Results'))}
                </button>
                <button
                  onClick={exportCSV}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                  style={{
                    backgroundColor: 'var(--color-bg-tertiary)',
                    color: 'var(--color-text-secondary)',
                    border: '1px solid var(--color-border)',
                  }}
                >
                  <Download size={13} />
                  {tr('导出结果CSV', 'Export CSV')}
                </button>
              </div>
            )}
          </div>

          {/* Running progress */}
          {running && currentRun && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs flex items-center gap-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                  <Loader size={12} className="animate-spin" />
                  {tr(
                    `运行任务 ${currentRun.taskIndex + 1}/${currentRun.totalTasks}，Agent ${currentRun.agentIndex + 1}/${currentRun.totalAgents}...`,
                    `Running task ${currentRun.taskIndex + 1}/${currentRun.totalTasks} on agent ${currentRun.agentIndex + 1}/${currentRun.totalAgents}...`,
                  )}
                </span>
                <span className="text-xs font-mono" style={{ color: 'var(--color-text-secondary)' }}>
                  {progress}%
                </span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{ width: `${progress}%`, backgroundColor: 'var(--color-accent)' }}
                />
              </div>
              <div className="mt-1.5 flex items-center gap-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                <Target size={12} />
                <span className="truncate">{currentRun.taskName}</span>
                <span>·</span>
                <span className="truncate" style={{ color: 'var(--color-accent)' }}>
                  {currentRun.agentName}
                </span>
              </div>
            </div>
          )}

          {/* Placeholder */}
          {!running && !completed && (
            <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
              <BarChart3 size={40} style={{ color: 'var(--color-text-muted)' }} />
              <p className="mt-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                {tr('配置任务和Agent后开始批量评测', 'Configure tasks and agents to start')}
              </p>
            </div>
          )}

          {/* Completed: comparison table + radar */}
          {completed && (
            <div className="space-y-6">
              {/* Comparison Table */}
              <div>
                <h3
                  className="text-sm font-semibold flex items-center gap-2 mb-3"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  <Table size={15} style={{ color: 'var(--color-accent)' }} />
                  {tr('对比表格', 'Comparison Table')}
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                        <th
                          className="text-left px-3 py-2 font-semibold"
                          style={{ color: 'var(--color-text-secondary)' }}
                        >
                          {tr('任务', 'Task')}
                        </th>
                        {enabledAgents.map((agent, ai) => (
                          <th
                            key={agent.id}
                            className="text-center px-3 py-2 font-semibold"
                            style={{ color: AGENT_COLORS[ai % AGENT_COLORS.length] }}
                          >
                            <span className="inline-flex items-center gap-1.5">
                              <span
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: AGENT_COLORS[ai % AGENT_COLORS.length] }}
                              />
                              {agent.name}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTasks.map(task => (
                        <tr key={task.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                          <td
                            className="px-3 py-2.5 text-left truncate max-w-[200px]"
                            style={{ color: 'var(--color-text-primary)' }}
                            title={task.title}
                          >
                            {task.title}
                          </td>
                          {enabledAgents.map((_agent, ai) => {
                            const cell = results[task.id]?.[ai]
                            return (
                              <td key={ai} className="px-3 py-2.5 text-center">
                                <button
                                  onClick={() => cell?.score && setSelectedCell({ taskId: task.id, agentIndex: ai })}
                                  disabled={!cell?.score}
                                  className="inline-flex flex-col items-center min-w-[52px] px-2 py-1 rounded"
                                  style={{
                                    cursor: cell?.score ? 'pointer' : 'default',
                                    opacity: cell ? 1 : 0.4,
                                  }}
                                >
                                  {cell?.error ? (
                                    <span className="text-xs" style={{ color: 'var(--color-danger)' }}>
                                      {tr('错误', 'Error')}
                                    </span>
                                  ) : cell?.score ? (
                                    <>
                                      <span className="text-base font-bold" style={{ color: scoreColor(cell.score.total) }}>
                                        {cell.score.total}
                                      </span>
                                      <CheckCircle size={10} style={{ color: scoreColor(cell.score.total) }} />
                                      {cell.judge && !cell.judge.error && (
                                        <div className="flex items-center gap-0.5 mt-0.5 text-[10px]" style={{ color: 'var(--color-accent)' }} title={cell.judge.rationale}>
                                          <Scale size={9} />
                                          <span>{cell.judge.overall}</span>
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <span style={{ color: 'var(--color-text-muted)' }}>-</span>
                                  )}
                                </button>
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                      {/* Average row */}
                      <tr style={{ borderTop: '2px solid var(--color-border)' }}>
                        <td
                          className="px-3 py-2.5 text-left font-bold"
                          style={{ color: 'var(--color-text-primary)' }}
                        >
                          {tr('平均', 'Average')}
                        </td>
                        {enabledAgents.map((_agent, ai) => {
                          const avg = averages[ai]
                          return (
                            <td key={ai} className="px-3 py-2.5 text-center">
                              {avg ? (
                                <span className="text-base font-bold" style={{ color: scoreColor(avg.avgTotal) }}>
                                  {avg.avgTotal.toFixed(0)}
                                </span>
                              ) : (
                                <span style={{ color: 'var(--color-text-muted)' }}>-</span>
                              )}
                            </td>
                          )
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="mt-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  {tr('点击单元格查看详细轨迹与评分', 'Click a cell to see detailed trace and score breakdown')}
                </p>
              </div>

              {/* Radar Chart */}
              <div>
                <h3
                  className="text-sm font-semibold flex items-center gap-2 mb-3"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  <BarChart3 size={15} style={{ color: 'var(--color-accent)' }} />
                  {tr('维度雷达图', 'Dimension Radar Chart')}
                </h3>
                <div style={{ width: '100%', height: 320 }}>
                  <ResponsiveContainer>
                    <RadarChart data={radarData} outerRadius="70%">
                      <PolarGrid stroke="var(--color-border)" />
                      <PolarAngleAxis
                        dataKey="dimension"
                        tick={{ fill: 'var(--color-text-secondary)', fontSize: 12 }}
                      />
                      {enabledAgents.map((agent, ai) => (
                        <Radar
                          key={agent.id}
                          name={agent.name}
                          dataKey={agent.name}
                          stroke={AGENT_COLORS[ai % AGENT_COLORS.length]}
                          fill={AGENT_COLORS[ai % AGENT_COLORS.length]}
                          fillOpacity={0.15}
                          strokeWidth={2}
                        />
                      ))}
                      <Legend
                        wrapperStyle={{ fontSize: 12, color: 'var(--color-text-secondary)' }}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Statistical Significance Section */}
              <div>
                <h3
                  className="text-sm font-semibold flex items-center gap-2 mb-3"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  <Sigma size={15} style={{ color: 'var(--color-accent)' }} />
                  {tr('统计显著性分析', 'Statistical Significance')}
                </h3>

                {/* Per-agent stats table */}
                <div className="overflow-x-auto mb-4">
                  <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                        <th className="text-left py-2 px-2" style={{ color: 'var(--color-text-secondary)' }}>{tr('Agent', 'Agent')}</th>
                        <th className="text-right py-2 px-2" style={{ color: 'var(--color-text-secondary)' }}>{tr('均值', 'Mean')}</th>
                        <th className="text-right py-2 px-2" style={{ color: 'var(--color-text-secondary)' }}>{tr('标准差', 'Std Dev')}</th>
                        <th className="text-right py-2 px-2" style={{ color: 'var(--color-text-secondary)' }}>95% CI</th>
                        <th className="text-right py-2 px-2" style={{ color: 'var(--color-text-secondary)' }}>{tr('最小', 'Min')}</th>
                        <th className="text-right py-2 px-2" style={{ color: 'var(--color-text-secondary)' }}>{tr('最大', 'Max')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enabledAgents.map((agent, ai) => {
                        const scores = selectedTasks
                          .map(t => results[t.id]?.[ai]?.score?.total)
                          .filter((s): s is number => typeof s === 'number')
                        if (scores.length === 0) return null
                        const mean = scores.reduce((s, v) => s + v, 0) / scores.length
                        const std = calcStdDev(scores)
                        const ci = calcConfidenceInterval(scores)
                        const min = Math.min(...scores)
                        const max = Math.max(...scores)
                        return (
                          <tr key={agent.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                            <td className="py-2 px-2 font-medium" style={{ color: AGENT_COLORS[ai % AGENT_COLORS.length] }}>{agent.name}</td>
                            <td className="text-right py-2 px-2" style={{ color: 'var(--color-text-primary)' }}>{mean.toFixed(1)}</td>
                            <td className="text-right py-2 px-2" style={{ color: 'var(--color-text-muted)' }}>±{std.toFixed(1)}</td>
                            <td className="text-right py-2 px-2" style={{ color: 'var(--color-text-muted)' }}>
                              {ci ? `[${ci.lower.toFixed(1)}, ${ci.upper.toFixed(1)}]` : '-'}
                            </td>
                            <td className="text-right py-2 px-2" style={{ color: 'var(--color-danger)' }}>{min.toFixed(0)}</td>
                            <td className="text-right py-2 px-2" style={{ color: 'var(--color-success)' }}>{max.toFixed(0)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pairwise t-test */}
                {enabledAgents.length >= 2 && (
                  <div>
                    <p className="text-xs mb-2" style={{ color: 'var(--color-text-muted)' }}>
                      {tr('Agent间差异显著性检验 (Welch t-test, p<0.05)', 'Pairwise significance test (Welch t-test, p<0.05)')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {enabledAgents.flatMap((agent1, i) =>
                        enabledAgents.slice(i + 1).map((agent2, j) => {
                          const scores1 = selectedTasks
                            .map(t => results[t.id]?.[i]?.score?.total)
                            .filter((s): s is number => typeof s === 'number')
                          const scores2 = selectedTasks
                            .map(t => results[t.id]?.[i + j + 1]?.score?.total)
                            .filter((s): s is number => typeof s === 'number')
                          const tTest = calcTTest(scores1, scores2)
                          if (!tTest) return null
                          return (
                            <div
                              key={`${i}-${j}`}
                              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs"
                              style={{
                                backgroundColor: tTest.significant ? 'rgba(74,222,128,0.08)' : 'var(--color-bg-tertiary)',
                                border: `1px solid ${tTest.significant ? 'rgba(74,222,128,0.2)' : 'var(--color-border)'}`,
                              }}
                            >
                              <span style={{ color: AGENT_COLORS[i % AGENT_COLORS.length] }}>{agent1.name}</span>
                              <span style={{ color: 'var(--color-text-muted)' }}>vs</span>
                              <span style={{ color: AGENT_COLORS[(i + j + 1) % AGENT_COLORS.length] }}>{agent2.name}</span>
                              <span style={{ color: 'var(--color-text-muted)' }}>|t|={tTest.t.toFixed(2)}</span>
                              {tTest.significant ? (
                                <span className="font-semibold" style={{ color: 'var(--color-success)' }}>
                                  {tr('显著差异', 'Significant')}
                                </span>
                              ) : (
                                <span style={{ color: 'var(--color-text-muted)' }}>
                                  {tr('无显著差异', 'Not significant')}
                                </span>
                              )}
                            </div>
                          )
                        }),
                      )}
                    </div>
                  </div>
                )}

                <p className="text-xs mt-3" style={{ color: 'var(--color-text-muted)' }}>
                  {tr(
                    '注：统计显著性需要至少2个任务的结果。置信区间为95% CI。t检验使用Welch近似。',
                    'Note: Statistical significance requires at least 2 task results. Confidence intervals are 95% CI. T-test uses Welch approximation.',
                  )}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ============ Detail Modal ============ */}
      {selectedCell &&
        (() => {
          const cell = results[selectedCell.taskId]?.[selectedCell.agentIndex]
          const task = tasks.find(t => t.id === selectedCell.taskId)
          const agent = enabledAgents[selectedCell.agentIndex]
          if (!cell || !task || !agent) return null
          return (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
              onClick={() => setSelectedCell(null)}
            >
              <div
                className="rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
                style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}
                onClick={e => e.stopPropagation()}
              >
                <div
                  className="flex items-center justify-between px-5 py-4 sticky top-0"
                  style={{
                    backgroundColor: 'var(--color-bg-card)',
                    borderBottom: '1px solid var(--color-border)',
                  }}
                >
                  <div>
                    <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                      {task.title}
                    </h3>
                    <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                      {tr('Agent', 'Agent')}: {agent.name} · {agent.mode}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedCell(null)}
                    className="p-1.5 rounded"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    ✕
                  </button>
                </div>

                <div className="p-5 space-y-4">
                  {cell.error ? (
                    <div
                      className="p-3 rounded-lg text-sm"
                      style={{ backgroundColor: 'rgba(248,113,113,0.1)', color: 'var(--color-danger)' }}
                    >
                      {cell.error}
                    </div>
                  ) : cell.score ? (
                    <>
                      {/* Total score */}
                      <div className="text-center">
                        <span className="text-4xl font-bold" style={{ color: scoreColor(cell.score.total) }}>
                          {cell.score.total}
                        </span>
                        <span className="text-lg" style={{ color: 'var(--color-text-muted)' }}>/100</span>
                      </div>
                      {/* Dimension breakdown */}
                      <div className="grid grid-cols-2 gap-3">
                        {DIMENSIONS.map(dim => {
                          const value = cell.score![dim.key] as number
                          const pct = cell.score!.breakdown[dim.key]
                          return (
                            <div
                              key={dim.key}
                              className="rounded-lg p-3"
                              style={{
                                backgroundColor: 'var(--color-bg-tertiary)',
                                border: '1px solid var(--color-border)',
                              }}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                                  {tr(dim.zh, dim.en)}
                                </span>
                                <span className="text-xs font-mono" style={{ color: 'var(--color-text-primary)' }}>
                                  {value}/{dim.max}
                                </span>
                              </div>
                              <div
                                className="h-1.5 rounded-full overflow-hidden"
                                style={{ backgroundColor: 'var(--color-bg-primary)' }}
                              >
                                <div
                                  className="h-full rounded-full"
                                  style={{ width: `${pct}%`, backgroundColor: 'var(--color-accent)' }}
                                />
                              </div>
                            </div>
                          )
                        })}
                      </div>
                      {/* Evidence */}
                      {cell.score.evidence.length > 0 && (
                        <div>
                          <p
                            className="text-[10px] font-semibold uppercase mb-2"
                            style={{ color: 'var(--color-text-muted)' }}
                          >
                            {tr('评分依据', 'Evidence')}
                          </p>
                          <div className="space-y-1.5">
                            {cell.score.evidence.map((ev, idx) => (
                              <div key={idx} className="flex items-start gap-1.5">
                                <CheckCircle
                                  size={11}
                                  className="mt-0.5 shrink-0"
                                  style={{ color: 'var(--color-success)' }}
                                />
                                <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                                  {ev}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : null}

                  {/* Trace steps */}
                  {cell.trace && (
                    <div>
                      <p
                        className="text-[10px] font-semibold uppercase mb-2"
                        style={{ color: 'var(--color-text-muted)' }}
                      >
                        {tr('执行轨迹', 'Trace')} ({cell.trace.steps?.length ?? 0} {tr('步', 'steps')})
                      </p>
                      <div className="space-y-1.5 max-h-64 overflow-y-auto">
                        {(cell.trace.steps ?? []).map((step, idx) => (
                          <div
                            key={idx}
                            className="rounded p-2 text-xs"
                            style={{
                              backgroundColor: 'var(--color-bg-tertiary)',
                              borderLeft: `3px solid ${
                                step.action.type === 'finish'
                                  ? 'var(--color-success)'
                                  : step.action.type === 'tool_call'
                                    ? 'var(--color-accent)'
                                    : step.action.type === 'error'
                                      ? 'var(--color-danger)'
                                      : '#c084fc'
                              }`,
                            }}
                          >
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="font-mono font-semibold" style={{ color: 'var(--color-text-muted)' }}>
                                #{step.step_number}
                              </span>
                              <span
                                className="text-[10px] uppercase font-semibold"
                                style={{ color: 'var(--color-text-secondary)' }}
                              >
                                {step.action.type}
                              </span>
                              {step.action.tool && (
                                <span className="text-[11px]" style={{ color: 'var(--color-accent)' }}>
                                  {step.action.tool}
                                </span>
                              )}
                            </div>
                            {step.action.reasoning && (
                              <p style={{ color: 'var(--color-text-secondary)' }}>{step.action.reasoning}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })()}
    </div>
  )
}
